import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Check, Music2, Plus, X } from 'lucide-react';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { useToast } from '@/contexts/ToastContext';
import { dataTransferHasFiles, getFileTypeLabel } from '@/components/ProjectFilesPopover/projectFilesPopoverUtils';
import { formatFileSizeRu } from '@/utils/formatFileSize';
import {
  collectAudioFiles,
  fileIdentity,
  FONOTEKA_AUDIO_ACCEPT,
  type RejectedAudioFile,
} from './audioFiles';
import { cn } from './cn';
import { Button } from './ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from './ui/card';

export type FonotekaUploadDialogProps = {
  open: boolean;
  packName: string;
  onClose: () => void;
  uploadFile: (file: File, onProgress: (pct: number) => void) => Promise<void>;
  onUploaded?: (count: number) => void;
};

type QueueStatus = 'queued' | 'uploading' | 'done' | 'error';

type QueueItem = {
  id: string;
  file: File;
  status: QueueStatus;
  progress: number;
  error?: string;
};

function rejectMessage(reason: RejectedAudioFile['reason']): string {
  switch (reason) {
    case 'not-audio':
      return 'не аудио';
    case 'too-large':
      return 'больше 100 МБ';
    default: {
      const _exhaustive: never = reason;
      return _exhaustive;
    }
  }
}

function fileSubtitle(item: QueueItem): string {
  const meta = `${getFileTypeLabel(item.file.name)} · ${formatFileSizeRu(item.file.size)}`;
  switch (item.status) {
    case 'queued':
      return meta;
    case 'uploading':
      return `${meta} · ${item.progress}%`;
    case 'done':
      return 'Готово';
    case 'error':
      return item.error ?? 'Не удалось загрузить';
    default: {
      const _exhaustive: never = item.status;
      return _exhaustive;
    }
  }
}

export function FonotekaUploadDialog({
  open,
  packName,
  onClose,
  uploadFile,
  onUploaded,
}: FonotekaUploadDialogProps) {
  const titleId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listViewportRef = useRef<HTMLElement | null>(null);
  const dragDepthRef = useRef(0);
  const busyRef = useRef(false);
  const itemsRef = useRef<QueueItem[]>([]);
  const { showError } = useToast();
  const [items, setItems] = useState<QueueItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [listFade, setListFade] = useState({ top: false, bottom: false });

  itemsRef.current = items;
  busyRef.current = busy;

  useEffect(() => {
    if (!open) return;
    setItems([]);
    setDragging(false);
    setBusy(false);
    setListFade({ top: false, bottom: false });
    busyRef.current = false;
    dragDepthRef.current = 0;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busyRef.current) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const syncListFade = useCallback(() => {
    const vp = listViewportRef.current;
    if (!vp) {
      setListFade({ top: false, bottom: false });
      return;
    }
    const overflow = vp.scrollHeight > vp.clientHeight + 1;
    if (!overflow) {
      setListFade({ top: false, bottom: false });
      return;
    }
    setListFade({
      top: vp.scrollTop > 2,
      bottom: vp.scrollTop + vp.clientHeight < vp.scrollHeight - 2,
    });
  }, []);

  useLayoutEffect(() => {
    syncListFade();
  }, [items, syncListFade]);

  const enqueue = useCallback(
    (incoming: Iterable<File>) => {
      if (busyRef.current) return;
      const incomingFiles = Array.from(incoming);
      let rejected: RejectedAudioFile[] = [];
      setItems((prev) => {
        const result = collectAudioFiles(
          incomingFiles,
          prev.map((item) => item.file),
        );
        rejected = result.rejected;
        if (result.accepted.length === 0) return prev;
        return [
          ...prev,
          ...result.accepted.map((file) => ({
            id: fileIdentity(file),
            file,
            status: 'queued' as const,
            progress: 0,
          })),
        ];
      });
      if (rejected.length === 0) return;
      const preview = rejected
        .slice(0, 3)
        .map((file) => `${file.name} (${rejectMessage(file.reason)})`)
        .join(', ');
      const extra = rejected.length > 3 ? ` и ещё ${rejected.length - 3}` : '';
      showError(`Пропущено: ${preview}${extra}`);
    },
    [showError],
  );

  const removeItem = useCallback((id: string) => {
    if (busyRef.current) return;
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const openFilePicker = useCallback(() => {
    if (busyRef.current) return;
    inputRef.current?.click();
  }, []);

  const closeIfIdle = useCallback(() => {
    if (!busyRef.current) onClose();
  }, [onClose]);

  const startUpload = useCallback(async () => {
    if (busyRef.current) return;
    const pending = itemsRef.current.filter(
      (item) => item.status === 'queued' || item.status === 'error',
    );
    if (pending.length === 0) return;
    busyRef.current = true;
    setBusy(true);
    let failed = 0;
    let uploaded = 0;
    for (const item of pending) {
      setItems((prev) =>
        prev.map((row) =>
          row.id === item.id ? { ...row, status: 'uploading', progress: 0, error: undefined } : row,
        ),
      );
      try {
        await uploadFile(item.file, (pct) => {
          setItems((prev) =>
            prev.map((row) => (row.id === item.id ? { ...row, progress: pct } : row)),
          );
        });
        uploaded += 1;
        setItems((prev) =>
          prev.map((row) =>
            row.id === item.id ? { ...row, status: 'done', progress: 100 } : row,
          ),
        );
      } catch (err) {
        failed += 1;
        const message = err instanceof Error ? err.message : 'Не удалось загрузить';
        setItems((prev) =>
          prev.map((row) =>
            row.id === item.id ? { ...row, status: 'error', error: message } : row,
          ),
        );
      }
    }
    busyRef.current = false;
    setBusy(false);
    if (failed === 0) {
      if (uploaded > 0) onUploaded?.(uploaded);
      onClose();
    }
  }, [onClose, onUploaded, uploadFile]);

  const pendingCount = items.filter((item) => item.status === 'queued' || item.status === 'error').length;
  const packLabel = packName.trim() || 'Без названия';

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          key="fonoteka-upload"
          className="fonoteka-portal fixed inset-0 z-[1200] flex items-center justify-center p-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          onDragEnter={(e) => {
            if (!dataTransferHasFiles(e.dataTransfer) || busy) return;
            e.preventDefault();
            dragDepthRef.current += 1;
            setDragging(true);
          }}
          onDragOver={(e) => {
            if (!dataTransferHasFiles(e.dataTransfer)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = busy ? 'none' : 'copy';
          }}
          onDragLeave={(e) => {
            if (!dataTransferHasFiles(e.dataTransfer)) return;
            e.preventDefault();
            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
            if (dragDepthRef.current === 0) setDragging(false);
          }}
          onDrop={(e) => {
            if (!dataTransferHasFiles(e.dataTransfer)) return;
            e.preventDefault();
            dragDepthRef.current = 0;
            setDragging(false);
            if (busy) return;
            enqueue(Array.from(e.dataTransfer.files));
          }}
        >
          <button
            type="button"
            className="absolute inset-0 block appearance-none border-0 bg-black/40 p-0"
            aria-label="Закрыть"
            onClick={closeIfIdle}
          />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 w-full max-w-md"
          >
            <Card
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              aria-busy={busy}
              className="max-h-[min(42rem,calc(100vh-2.5rem))] w-full shadow-lg"
            >
        <CardHeader className="shrink-0">
          <CardTitle id={titleId}>Загрузка треков</CardTitle>
          <CardDescription className="truncate" title={packLabel}>
            в проект «{packLabel}»
          </CardDescription>
          <CardAction>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Закрыть"
              disabled={busy}
              onClick={closeIfIdle}
            >
              <X />
            </Button>
          </CardAction>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col gap-5 overflow-hidden">
          <div
            role="button"
            tabIndex={busy ? -1 : 0}
            aria-disabled={busy}
            onClick={() => {
              if (!busy) openFilePicker();
            }}
            onKeyDown={(e) => {
              if (busy) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openFilePicker();
              }
            }}
            className={cn(
              'flex w-full flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border bg-muted/60 px-5 py-8 text-center',
              dragging && 'border-foreground/25 bg-muted',
              busy && 'pointer-events-none opacity-50',
            )}
          >
            <span className="flex size-9 items-center justify-center rounded-xl bg-background text-muted-foreground ring-1 ring-foreground/5">
              <Plus className="size-4" />
            </span>
            <span className="text-sm font-medium">Перетащите аудио сюда</span>
            <span className="text-sm text-muted-foreground">или нажмите, чтобы выбрать файлы</span>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={FONOTEKA_AUDIO_ACCEPT}
            multiple
            className="hidden"
            disabled={busy}
            onChange={(e) => {
              enqueue(Array.from(e.target.files ?? []));
              e.target.value = '';
            }}
          />

          {items.length > 0 ? (
            <div className="fonoteka-upload-list">
              <OverlayScrollArea
                defer={false}
                className="fonoteka-upload-list__scroll"
                viewportRef={listViewportRef}
                onViewportElement={() => syncListFade()}
                onScroll={syncListFade}
              >
                <ul className="m-0 flex list-none flex-col gap-4 px-4">
                  {items.map((item) => (
                    <li key={item.id} className="flex items-center gap-4">
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                        <Music2 className="size-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{item.file.name}</div>
                        <div
                          className={cn(
                            'truncate text-sm text-muted-foreground',
                            item.status === 'error' && 'text-destructive',
                          )}
                        >
                          {fileSubtitle(item)}
                        </div>
                        {item.status === 'uploading' ? (
                          <span className="mt-2 block h-1 overflow-hidden rounded-full bg-muted" aria-hidden>
                            <span
                              className="block h-full rounded-full bg-primary"
                              style={{ width: `${item.progress}%` }}
                            />
                          </span>
                        ) : null}
                      </div>
                      {item.status === 'done' ? (
                        <Check className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="shrink-0"
                          aria-label={`Убрать ${item.file.name}`}
                          disabled={busy || item.status === 'uploading'}
                          onClick={() => removeItem(item.id)}
                        >
                          <X />
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              </OverlayScrollArea>
              {listFade.top ? (
                <div className="fonoteka-upload-list__fade fonoteka-upload-list__fade_top" aria-hidden />
              ) : null}
              {listFade.bottom ? (
                <div className="fonoteka-upload-list__fade fonoteka-upload-list__fade_bottom" aria-hidden />
              ) : null}
            </div>
          ) : null}
        </CardContent>

        <CardFooter className="shrink-0 gap-2">
          <Button type="button" variant="outline" size="lg" className="flex-1" disabled={busy} onClick={closeIfIdle}>
            Отмена
          </Button>
          <Button
            type="button"
            size="lg"
            className="flex-1"
            disabled={busy || pendingCount === 0}
            onClick={() => void startUpload()}
          >
            {busy ? 'Загрузка…' : 'Загрузить'}
          </Button>
        </CardFooter>
      </Card>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
